// Pure helpers for the AI prescription assistant
// (components/dyrejournal/prescriptionAiAssistant.js). No stores, no JSX, never
// mutates input. proposalToPrefill mirrors the drug-line fields that
// addPrescription.js searchDrugCallback writes, so an accepted proposal produces
// exactly the same kind of line as a manual drug search.

const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };

function rank(severity) {
    return severity in SEVERITY_RANK ? SEVERITY_RANK[severity] : 3;
}

function lineIdentity(d) {
    return d.lId || d.navnFormStyrke || d.rpText || '';
}

// One token per drawer line, duplicates kept and the dosage included: a second copy of
// the same product, a changed dose, or a manually created drug without lId must all
// trigger a new check.
export function drugSetKey(drugs) {
    return (Array.isArray(drugs) ? drugs : [])
        .filter(d => d && lineIdentity(d))
        .map(d => `${lineIdentity(d)}::${String(d.dosage || '').trim()}`)
        .sort()
        .join('|');
}

export function drugsToCheckPayload(drugs) {
    return (Array.isArray(drugs) ? drugs : [])
        .filter(d => d && lineIdentity(d))
        .map(d => ({
            lId: d.lId || null,
            atcV: d.atcV || null,
            atcDn: d.atcDn || null,
            navnFormStyrke: d.navnFormStyrke || d.rpText || '',
            dosage: d.dosage || '',
        }));
}

export function proposalToPrefill(proposal) {
    const p = proposal || {};
    return {
        qty: p.qty > 0 ? p.qty : 1,
        dosage: p.dosage || '',
        frequency: '',
        diagnoseText: p.diagnoseText || '',
        usageText: p.usageText || '',
        additionalNotes: p.additionalNotes || '',
        reit: p.reit > 0 ? p.reit : '',
    };
}

export function severityClass(severity) {
    if (severity === 'high') return 'alert-danger';
    if (severity === 'medium') return 'alert-warning';
    return 'alert-info';
}

export function mergeWarnings(proposalWarnings, checkWarnings) {
    const seen = new Set();
    const out = [];
    [...(proposalWarnings || []), ...(checkWarnings || [])].forEach(w => {
        if (!w || !w.text) return;
        const key = `${w.source || 'ai'}|${w.type}|${[...(w.drugs || [])].sort().join(',')}|${w.text}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(w);
    });
    return out.sort((a, b) => rank(a.severity) - rank(b.severity));
}

function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Does a package name carry the strength the model dosed for? Mirrors the backend
// helper in backend/lib/prescription-ai-context.js: tolerant on the decimal separator
// and whitespace, the unit must follow the number and end there.
export function strengthMatches(preferredStrength, text) {
    const m = String(preferredStrength || '').match(/(\d+(?:[.,]\d+)?)\s*([a-zA-Zµ%]+(?:\/[a-zA-Z]+)?)/);
    if (!m || !text) return false;
    const num = m[1].replace(/[.,]/, '[.,]');
    const unit = escapeRegExp(m[2]);
    // The unit must end there: "5 mg" must not match "5 mg/ml", so a slash also breaks the match.
    const re = new RegExp(`(^|[^\\d.,])${num}\\s*${unit}(?![a-zA-Z/])`, 'i');
    return re.test(String(text));
}

// Which package may be preselected for a proposal: the only match, or the first match
// carrying the strength the model dosed for. Otherwise null — the vet must choose, because
// the dosage text was written for a specific strength and must not silently follow another.
export function preselectedLId(proposal) {
    const matched = proposal && Array.isArray(proposal.matchedDrugs) ? proposal.matchedDrugs : [];
    if (matched.length === 0) return null;
    if (matched.length === 1) return matched[0].lId || null;
    const hit = matched.find(d => strengthMatches(proposal.preferredStrength, d.navnFormStyrke));
    return hit ? hit.lId : null;
}

// Package chips must be distinguishable: FEST names are identical across pack sizes.
export function packageLabel(drug) {
    const d = drug || {};
    const size = d.pakningsinfoPakningsstr || d.pakningsinfoMengde;
    const pack = [size, d.pakningsinfoEnhetPakningV].filter(Boolean).join(' ');
    const name = d.navnFormStyrke || '';
    return pack ? `${name} · ${pack}` : name;
}
